// Sollicitation unique « Comment ça se passe ? » (item C, 2026-07-23).
// L'état durable vit sur profiles.feedback_prompt_status (NULL | answered |
// declined | later) — en base, pas en localStorage, pour tenir le
// multi-appareils : « Non merci » sur iPhone vaut aussi sur le web.
//
// Le marqueur de session (« Plus tard » cliqué il y a un instant) est la seule
// exception locale : sans lui la carte reviendrait au simple retour à
// l'accueil, alors que le critère dit « à une session ultérieure ».

import type { SupabaseClient } from "@supabase/supabase-js";

export type FeedbackPromptStatus = "answered" | "declined" | "later";

export const FEEDBACK_PROMPT_MIN_CAPTURES = 3;

// Questions guides du mode sollicité (note de décision 2026-07-22 ;
// formulations à valider par Gabrielle à la porte QA fonctionnelle).
export const FEEDBACK_PROMPT_QUESTIONS = [
  "Qu'est-ce qui vous a été le plus utile jusqu'ici ?",
  "Qu'est-ce qui vous a freiné ou agacé ?",
  "Que manquerait-il pour que vous l'utilisiez chaque semaine ?",
] as const;

const LATER_SESSION_KEY = "captureBesoins.feedbackPromptLater";

export type FeedbackPromptResult<T> = ({ ok: true } & T) | { ok: false; error: string };

export interface FeedbackPromptInput {
  client: SupabaseClient;
  userId: string;
}

// Admissibilité côté base uniquement (le marqueur de session se vérifie à
// part) : statut NULL ou later ET ≥ 3 captures créées par le compte, tous
// projets confondus. Toute erreur = pas de carte (fail-closed : rater une
// sollicitation est bénin, la re-poser à quelqu'un qui a refusé ne l'est pas).
export async function fetchFeedbackPromptEligibility(
  input: FeedbackPromptInput,
): Promise<FeedbackPromptResult<{ eligible: boolean }>> {
  const { data, error } = await input.client
    .from("profiles")
    .select("feedback_prompt_status")
    .eq("id", input.userId)
    .maybeSingle();
  if (error) return { ok: false, error: error.message };

  const status = data?.feedback_prompt_status ?? null;
  if (status === "answered" || status === "declined") return { ok: true, eligible: false };

  const { count, error: countError } = await input.client
    .from("captures")
    .select("id", { count: "exact", head: true })
    .eq("created_by", input.userId);
  if (countError) return { ok: false, error: countError.message };

  return { ok: true, eligible: (count ?? 0) >= FEEDBACK_PROMPT_MIN_CAPTURES };
}

export async function setFeedbackPromptStatus(
  input: FeedbackPromptInput & { status: FeedbackPromptStatus },
): Promise<FeedbackPromptResult<object>> {
  const { error } = await input.client
    .from("profiles")
    .update({ feedback_prompt_status: input.status })
    .eq("id", input.userId);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export function isFeedbackPromptDeferredThisSession(): boolean {
  return sessionStorage.getItem(LATER_SESSION_KEY) === "1";
}

export function markFeedbackPromptDeferredThisSession(): void {
  sessionStorage.setItem(LATER_SESSION_KEY, "1");
}
